import React, { useEffect, useMemo } from "react";
import {
  Radio,
  Stack,
  Box,
  RadioGroup,
  Divider,
  Columns,
  Column,
  Text,
} from "@origin-digital/ods-core";
import { AccountPayer, FormValues, GasOptionEnum } from "../types";
import { useFormikContext } from "formik";
import { accountBlockInitialValues } from "../FormComponents/Validations";
import { GasOptions } from "../FormComponents/GasOptions";
import ReadOnlyFieldWrapper from "../ReadOnlyFieldWrapper";
import FormFieldLayout from "../FormComponents/FormFieldLayout";

const HomeUserFuelInPremise = ({ isReadOnly }: { isReadOnly?: boolean }) => {
  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const { selectedConnections, gasOptions } = values;

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
    ]
  );

  const calculatedErrors = useMemo(() => {
    return (
      errors.electricityAccountPayer ||
      errors.gasLine?.accountPayer ||
      errors.gasMeter?.accountPayer ||
      errors.both?.accountPayer
    );
  }, [errors]);

  useEffect(() => {
    if (values.premiseAddress.state === "ACT") {
      setFieldValue("gasOptions", [GasOptionEnum.BOTH]);
    }
  }, [values.premiseAddress.state]);

  const updateFuelPayers = (accountPayer: AccountPayer) => {
    selectedConnections.forEach((connection) => {
      if (connection === "Electricity") {
        setFieldValue("electricityAccountPayer", accountPayer);
      }
      if (connection === "Gas") {
        gasOptions.forEach((option) => {
          setFieldValue(`${option}.accountPayer`, accountPayer);
        });
      }
    });
  };

  const calculatedValue = accountPayers.includes(AccountPayer.SOMEONE_ELSE)
    ? AccountPayer.SOMEONE_ELSE
    : AccountPayer.COMPANY;
  const valueToDisplay = accountPayers.includes(AccountPayer.SOMEONE_ELSE)
    ? "Customer"
    : "Me/My Company";

  const showGasOptions =
    values.selectedConnections.includes("Gas") &&
    values.premiseAddress.state !== "ACT";

  return (
    <Stack>
      {showGasOptions && (
        <Box marginBottom="xxlarge">
          <GasOptions homeUser={true} />
        </Box>
      )}
      {isReadOnly ? (
        <ReadOnlyFieldWrapper value={true}>
          <FormFieldLayout
            multipleFields
            text={
              <>
                If you need to assign different account holders for electricity
                and natural gas, you will need to raise separate requests.
              </>
            }
          >
            <Columns
              collapseBelow="md"
              space={{
                xs: "none",
                md: "xlarge",
              }}
              alignY="stretch"
            >
              {/* Fields */}
              <Column width="2/3">
                <Stack space="xsmall">
                  <Stack space="xxsmall">
                    <Text variant="body-small" tone="neutralLight">
                      Who’s responsible for the accounts?
                    </Text>
                    <Text>{valueToDisplay}</Text>
                  </Stack>
                  <Divider />
                </Stack>
              </Column>
              {/* Fields */}
              {/* HelperText */}
              <Column width="1/3" />
              {/* End HelperText */}
            </Columns>
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
      ) : (
        <FormFieldLayout
          multipleFields
          text={
            <>
              If you need to assign different account holders for electricity
              and natural gas, you will need to raise separate requests.
            </>
          }
        >
          <RadioGroup
            id="combinedAccountPayer"
            name="combinedAccountPayer"
            label="Who's responsible for the account(s)?"
            error={!!calculatedErrors}
            helperText={calculatedErrors}
            value={calculatedValue}
            onChange={(e) => {
              Object.keys(accountBlockInitialValues).forEach((k) => {
                setFieldValue(
                  `${k}`,
                  accountBlockInitialValues[
                    k as keyof typeof accountBlockInitialValues
                  ]
                );
              });
              updateFuelPayers(e.target.value as AccountPayer);
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              flexDirection={{
                xs: "column",
                sm: "column",
              }}
            >
              <Radio
                id="my-company"
                label={"Me/My company"}
                helperText="Will be responsible for any charges until the account is closed."
                value={AccountPayer.COMPANY}
              />
              <Radio
                id="customer"
                label="Someone else"
                helperText="Will be responsible for any charges until the account is closed."
                value={AccountPayer.SOMEONE_ELSE}
              />
            </Box>
          </RadioGroup>
        </FormFieldLayout>
      )}
    </Stack>
  );
};

export default HomeUserFuelInPremise;
